import { fabric } from "fabric";
import { FabricObject } from "./renderer/fabric-extended-types";
import { modifyItem } from "./state/items";
import { DocumentDesignState } from "./state/types";

export function rearrangeItems(
  canvas: fabric.Canvas,
  state: DocumentDesignState,
): DocumentDesignState {
  const relativePositionObjectIds = new Set(
    state.items
      .filter((elem) => elem.position === "relative")
      .map((elem) => elem.id),
  );

  if (relativePositionObjectIds.size === 0) {
    return state;
  }

  let updatedState = state;

  // Order the canvas objects by their top position
  // the vertical order of rendered items should always be the same as this array's order
  const orderedCanvasObjects = canvas
    .getObjects()
    .filter((elem) => elem.visible !== false)
    .sort((a, b) => (a.top ?? 0) - (b.top ?? 0)) as FabricObject[];

  for (let i = 1; i < orderedCanvasObjects.length; i++) {
    const current = orderedCanvasObjects[i];

    if (!relativePositionObjectIds.has(current.id)) {
      // ignore non-relative objects
      continue;
    }

    // tracks the object with the biggest top + height position
    // i.e. the relative object should be moved right after the `bigger` object
    let bigger = orderedCanvasObjects[i - 1];

    // iterate over previous objects to check if the current relative object intersects
    // with any of them, then move it after `bigger` object
    for (let j = i - 1; j >= 0; j--) {
      const check = orderedCanvasObjects[j];

      if (
        (check.top ?? 0) + (check.height ?? 0) >
        (bigger.top ?? 0) + (bigger.height ?? 0)
      ) {
        bigger = check;
      }

      if (intersects(check, current)) {
        const gapToCurrent = (bigger.top ?? 0) + (bigger.height ?? 0) + 10;
        current.top = gapToCurrent;
        current.setCoords();
        updatedState = modifyItem(updatedState, current.id, {
          top: gapToCurrent,
        });
      }
    }
  }

  return updatedState;
}

// For some reason the coords of the current object, when it's a table object and the table
// is being resized, has the wrong coords when its being compared with the next object.
// So instead of using a.bl.y we use a.tl.y + current.height
function intersects(current: fabric.Object, next: fabric.Object): boolean {
  if (!current.aCoords || !next.aCoords || !current.height) return false;
  const a = current.aCoords;
  const b = next.aCoords;
  return (
    a.tl.x < b.tr.x &&
    a.tr.x > b.tl.x &&
    a.tl.y < b.bl.y &&
    a.tl.y + current.height > b.tl.y
  );
}
